import { always, equals, isEmpty, keys, omit, pick } from 'ramda';
import type { Location, Node, NodeEntry, Point } from 'slate';
import { Editor, Element, Path, Range, Text } from 'slate';

import type { Length } from '../../types';
import { convertUnit, inToPx, mapObjectValues, pxToIn } from '../../util';
import type {
  AlignmentType,
  AlineText,
  AlineTextFormats,
  IndentationProperties,
  ListLevel,
  ListMarker,
  NodeMatch,
  PageDimensions,
  ParagraphBorders,
  ParagraphElement,
  ParagraphPropKey,
  ParagraphProps,
  ParagraphStyleVariant,
  PartialParagraphProps,
  TableBorder,
} from '../internal';
import {
  applySuggestions,
  defaultStyleVariant,
  isDocumentElement,
  isFirstLineIndentEdit,
  isParagraphElement,
  isTableElement,
  ListLevelAlignment,
  measureText,
  paragraphPropKeys,
  paragraphType,
  Queries,
} from '../internal';

export const tabWidthIn = 0.5;

export interface ParagraphBorderSibling {
  firstLineIndentPx: number;
  widthPx: number;
  borders?: ParagraphBorders;
}

export interface ParagraphBordersToDraw {
  bordersToDraw: ParagraphBorders | undefined;
  /** Whether this paragraph continues into the next/previous one as one merged border box. */
  mergesWithNext: boolean;
  mergesWithPrev: boolean;
}

export interface ParagraphBorderLayoutInsets {
  bottom: number;
  left: number;
  right: number;
  top: number;
}

interface SpacingAfterProps extends Pick<ParagraphProps, 'spacingAfter' | 'suggestedEdits'> {
  spacingAfterSuppressed?: boolean;
}

interface SpacingBeforeProps extends Pick<ParagraphProps, 'spacingBefore' | 'suggestedEdits'> {
  spacingBeforeSuppressed?: boolean;
}

/**
 * Returns the nearest qualifying paragraph relative to `point`: the paragraph
 * the point sits in (distance 0), then the nearest match above, then below.
 */
function getNearestParagraphMatch(
  editor: Editor,
  point: Point,
  match: (node: Node, path: Path) => boolean
): NodeEntry<ParagraphElement> | null {
  const containing = Editor.above<ParagraphElement>(editor, { at: point, match });
  if (containing) return containing;

  const previous = Editor.previous<ParagraphElement>(editor, { at: point, match });
  if (previous) return previous;

  return Editor.next<ParagraphElement>(editor, { at: point, match }) ?? null;
}

/**
 * True for paragraphs that are direct children of the document element.
 * Excludes paragraphs nested in tables, headers, and footers. The document is
 * not necessarily at index 0.
 */
function isBodyParagraphPath(editor: Editor, path: Path): boolean {
  if (path.length !== 2) return false;
  const root = editor.children[path[0]];
  return Boolean(root) && isDocumentElement(root);
}

export const paragraphQueries = {
  /**
   * Returns the adjusted first line indent *in inches*, taking into account suggested edits.
   */
  adjustedFirstLineIndentIn: (
    editor: Editor,
    options: {
      listMarker: ListMarker | null;
      paragraph: ParagraphProps;
    }
  ): {
    firstLineIndentAdjustedValIn: number;
    listMarkerBufferIn: number;
    listMarkerTextWidthIn: number;
  } => {
    const { listMarker, paragraph } = options;
    const leftIndentIn = Queries.renderedParagraphLeftIndent(paragraph);
    const activeListProps = Queries.renderedParagraphListProps(paragraph);
    const firstLineIndentEdit = paragraph.suggestedEdits?.find(isFirstLineIndentEdit);
    const firstLineIndentIn =
      firstLineIndentEdit?.value?.indentationFirstLine ?? paragraph.indentationFirstLine ?? 0;
    const diff = firstLineIndentIn - leftIndentIn;
    const roundToPointFive = Math.floor(diff * 2) / 2;
    const nearestPointFive = leftIndentIn + roundToPointFive;
    // The buffer keeps the list marker w/in .5 in of the left edge of the line.
    const listMarkerBufferIn = tabWidthIn - (firstLineIndentIn - nearestPointFive);
    const listMarkerTextWidthIn = listMarker
      ? pxToIn(measureText(editor, { textNode: listMarker }))
      : 0;
    // Only apply the width of the marker if the alignment for this level is start.  End (right) alignment has no impact.
    const adjustedListMarkerWidthIn =
      listMarker &&
      Queries.activeListLevelAlignment(listMarker.listLevelDefinition) === ListLevelAlignment.Start
        ? listMarkerTextWidthIn
        : 0;
    const applyListItemIndent = firstLineIndentIn >= -adjustedListMarkerWidthIn;
    const isListItem = Boolean(activeListProps);

    return {
      firstLineIndentAdjustedValIn: isListItem
        ? applyListItemIndent
          ? firstLineIndentIn + adjustedListMarkerWidthIn + listMarkerBufferIn
          : 0
        : firstLineIndentIn,
      listMarkerBufferIn: applyListItemIndent ? listMarkerBufferIn : firstLineIndentIn,
      listMarkerTextWidthIn,
    };
  },

  /**
   * Returns the adjusted first line indent *in pixels*, taking into account suggested edits.
   */
  adjustedFirstLineIndentPx: (
    editor: Editor,
    options: {
      listMarker: ListMarker | null;
      paragraph: ParagraphProps;
    }
  ): {
    firstLineIndentAdjustedValPx: number;
    listMarkerBufferPx: number;
    listMarkerTextWidthPx: number;
  } => {
    const { firstLineIndentAdjustedValIn, listMarkerBufferIn, listMarkerTextWidthIn } =
      Queries.adjustedFirstLineIndentIn(editor, options);

    return {
      firstLineIndentAdjustedValPx: inToPx(firstLineIndentAdjustedValIn),
      listMarkerBufferPx: inToPx(listMarkerBufferIn),
      listMarkerTextWidthPx: inToPx(listMarkerTextWidthIn),
    };
  },

  /**
   * Returns several paragraph props - e.g., alignment, style variant, and line spacing - that are active at a location.
   */
  consistentParagraphProps: (
    editor: Editor,
    options: { at?: Location } = {}
  ): PartialParagraphProps => {
    const { at = editor.selection } = options;
    if (!at) return {};

    const propGetters = Queries.getParagraphPropGetters();
    const paragraphEntries = Queries.paragraphsAtLocation(editor, { at, listMarker: true });
    const firstParagraphEntry = paragraphEntries.next().value;
    if (!firstParagraphEntry) return {};

    const [firstParagraph] = firstParagraphEntry;
    const activeProps = Queries.getActiveParagraphProps(firstParagraph);

    for (const [paragraph] of paragraphEntries) {
      for (const key of keys(Queries.getParagraphProps(paragraph))) {
        if (!equals(activeProps[key], propGetters[key](paragraph))) delete activeProps[key];
      }

      if (isEmpty(activeProps)) break;
    }

    return activeProps;
  },

  /**
   * Finds the nearest paragraph that renders `styleVariant`, searching outward
   * from `at`: the paragraph at the location first, then upward, then downward.
   * Returns null when nothing matches.
   *
   * Used to borrow paragraph spacing (before/after/line) from existing content
   * when inserting from HTML, so an insertion matches the document's actual
   * spacing instead of a fixed default that is often wrong.
   *
   * Scope rules:
   * - When `at` is inside a table, paragraphs within that same table are
   *   searched first (a table keeps its own spacing); only if none match there
   *   do we fall out to the body.
   * - The body search considers only top-level paragraphs (direct children of
   *   the document). When `at` is NOT in a table we deliberately never descend
   *   into tables — a body insertion shouldn't borrow a table cell's spacing.
   */
  findNearestParagraphByStyleVariant: (
    editor: Editor,
    options: { at: Location; styleVariant: ParagraphStyleVariant }
  ): NodeEntry<ParagraphElement> | null => {
    const { at, styleVariant } = options;
    const point = Editor.point(editor, at, { edge: 'start' });

    const matchesVariant = (node: Node): boolean =>
      isParagraphElement(node) && Queries.renderedParagraphStyleVariant(node) === styleVariant;

    const tableEntry = Editor.above(editor, { at: point, match: isTableElement });
    if (tableEntry) {
      const [, tablePath] = tableEntry;
      const withinTable = getNearestParagraphMatch(
        editor,
        point,
        (node, path) => matchesVariant(node) && Path.isDescendant(path, tablePath)
      );
      if (withinTable) return withinTable;
    }

    return getNearestParagraphMatch(
      editor,
      point,
      (node, path) => matchesVariant(node) && isBodyParagraphPath(editor, path)
    );
  },

  firstTextNodeFormats: (editor: Editor, path: Path): AlineTextFormats | undefined => {
    const [entry] = Editor.nodes<AlineText>(editor, {
      at: path,
      match: Text.isText,
    });
    if (!entry) return;

    const [text] = entry;

    return {
      bold: {
        active: Queries.isBoldActive(text),
        origin: 'INHERITED',
      },
      fontColor: {
        color: Queries.renderedFontColor(text),
        origin: 'INHERITED',
      },
      fontSize: {
        origin: 'INHERITED',
        size: Queries.activeFontSizePt(text),
      },
      fontType: {
        fontFamily: Queries.activeFontFamily(text),
        origin: 'INHERITED',
        type: Queries.activeFontType(text),
      },
      highlight: {
        color: Queries.renderedHighlightColor(text),
        origin: 'INHERITED',
      },
      italic: {
        active: Queries.isItalicActive(text),
        origin: 'INHERITED',
      },
      strikethrough: {
        active: Queries.isStrikethroughActive(text),
        origin: 'INHERITED',
      },
      underline: {
        active: Queries.isUnderlineActive(text),
        origin: 'INHERITED',
      },
      verticalAlign: {
        align: Queries.activeVerticalAlign(text),
        origin: 'INHERITED',
      },
    };
  },

  /**
   * Pass an array of keys and get back the relevant paragraph props.
   */
  getActiveParagraphProps: <T extends Partial<ParagraphProps>>(
    paragraph: ParagraphProps,
    propKeys?: ParagraphPropKey[]
  ): T => {
    const kys = propKeys ?? paragraphPropKeys;
    const getters = pick(kys, Queries.getParagraphPropGetters());
    return mapObjectValues(getters, val => val(paragraph)) as T;
  },

  /**
   * Word merges consecutive paragraphs with identical border settings into one
   * continuous box (ECMA-376 §17.3.1). The shared top/bottom edges are replaced
   * by the `between` border when one is set, and suppressed otherwise. The
   * `between` border is emitted as the `bottom` of the earlier paragraph so it
   * is drawn once per adjacent pair.
   */
  getParagraphBordersToDraw(options: {
    borders: ParagraphBorders | undefined;
    firstLineIndentPx: number;
    widthPx: number;
    next?: ParagraphBorderSibling | null;
    prev?: ParagraphBorderSibling | null;
  }): ParagraphBordersToDraw {
    const { borders, firstLineIndentPx, next, prev, widthPx } = options;
    if (!borders) return { bordersToDraw: undefined, mergesWithNext: false, mergesWithPrev: false };

    function mergesWith(sibling: ParagraphBorderSibling | null | undefined): boolean {
      if (!sibling?.borders || sibling.widthPx !== widthPx) return false;
      const hasNegativeFirstLineIndent = firstLineIndentPx < 0 || sibling.firstLineIndentPx < 0;
      if (hasNegativeFirstLineIndent && sibling.firstLineIndentPx !== firstLineIndentPx)
        return false;

      return equals(sibling.borders, borders);
    }

    const mergesWithNext = mergesWith(next);
    const mergesWithPrev = mergesWith(prev);

    const { between, ...bordersToDraw } = borders;
    if (mergesWithPrev) delete bordersToDraw.top;
    if (mergesWithNext) {
      if (between) bordersToDraw.bottom = between;
      else delete bordersToDraw.bottom;
    }

    return { bordersToDraw, mergesWithNext, mergesWithPrev };
  },

  /**
   * Returns the paragraph entry with the given locationId, or undefined if not found.
   */
  getParagraphByLocationId: (
    editor: Editor,
    locationId: string
  ): NodeEntry<ParagraphElement> | undefined => {
    const [entry] = Editor.nodes<ParagraphElement>(editor, {
      at: [],
      match: node => isParagraphElement(node) && node.locationId === locationId,
    });

    return entry;
  },

  /**
   * Returns a map of paragraph prop key (e.g., alignment) to its respective getter.
   */
  getParagraphPropGetters: (): Record<
    keyof ParagraphProps,
    (paragraph: ParagraphProps) => ParagraphProps[keyof ParagraphProps]
  > => {
    return {
      alignment: Queries.renderedParagraphAlignment,
      borders: Queries.renderedParagraphBorders,
      contextualSpacing: paragraph => paragraph.contextualSpacing,
      glyphProps: paragraph => paragraph.glyphProps,
      indentationFirstLine: Queries.renderedParagraphFirstLineIndent,
      indentationLeft: Queries.renderedParagraphLeftIndent,
      indentationRight: Queries.renderedParagraphRightIndent,
      lineSpacing: Queries.renderedParagraphLineSpacing,
      listLevel: Queries.renderedParagraphListLevel,
      listProps: Queries.renderedParagraphListProps,
      locationId: paragraph => paragraph.locationId,
      shade: Queries.renderedParagraphShade,
      spacingAfter: Queries.renderedParagraphSpacingAfter,
      spacingBefore: Queries.renderedParagraphSpacingBefore,
      suggestedEdits: paragraph => paragraph.suggestedEdits,
      type: paragraph => paragraph.type,
      variant: Queries.renderedParagraphStyleVariant,
    };
  },

  /**
   * Returns a paragraph's props.
   */
  getParagraphProps: (paragraph: ParagraphElement): ParagraphProps => omit(['children'], paragraph),

  /**
   * Return the indentation properties at the current focus point.
   */
  indentationProperties: (editor: Editor): IndentationProperties => {
    if (!editor.selection) return { firstLine: 0, left: 0, right: 0 };

    const [start] = Range.edges(editor.selection);
    const entry = Editor.above<ParagraphElement>(editor, {
      at: start,
      match: node => Element.isElement(node) && node.type === paragraphType,
    });
    if (!entry) return { firstLine: 0, left: 0, right: 0 };

    const [paragraph] = entry;

    return {
      firstLine: Queries.renderedParagraphFirstLineIndent(paragraph),
      left: Queries.renderedParagraphLeftIndent(paragraph),
      right: Queries.renderedParagraphRightIndent(paragraph),
    };
  },

  indentationValid: (
    editor: Editor,
    paragraph: ParagraphProps,
    options: {
      indentationProperties?: Partial<IndentationProperties>;
      pageDimensions?: Partial<PageDimensions>;
    }
  ): boolean => {
    const pageDimensions = {
      ...Queries.activePageDimensions(editor, 'in'),
      ...options.pageDimensions,
    };
    const indentationProperties = {
      firstLine:
        options.indentationProperties?.firstLine ??
        Queries.renderedParagraphFirstLineIndent(paragraph),
      left: options.indentationProperties?.left ?? Queries.renderedParagraphLeftIndent(paragraph),
      right:
        options.indentationProperties?.right ?? Queries.renderedParagraphRightIndent(paragraph),
    };
    const { marginLeft, marginRight, width: pageWidth } = pageDimensions;
    const { firstLine, left, right } = indentationProperties;
    const paragraphWidth = pageWidth - marginLeft - marginRight - left - right;
    const isIndentationInvalid =
      paragraphWidth < 1 ||
      paragraphWidth - firstLine < 1 ||
      left + marginLeft < 0 ||
      left + firstLine + marginLeft < 0 ||
      right + marginRight < 0;

    return !isIndentationInvalid;
  },

  paragraphAtPoint: (editor: Editor, options: { at?: Point } = {}) => {
    const { at = editor?.selection?.focus } = options;

    if (!at) {
      console.warn(`The at parameter is ${at}.  Pass a valid Point.`);
      return;
    }

    return Editor.above<ParagraphElement>(editor, {
      at,
      match: n => Element.isElement(n) && n.type === paragraphType,
    });
  },

  /**
   * Validate paragraph props before setting them in the editor.
   */
  paragraphPropsValid: (
    editor: Editor,
    paragraph: ParagraphProps,
    newProps: PartialParagraphProps
  ): boolean => {
    type ParagraphPropKey = keyof PartialParagraphProps;
    const keysToValidate: ParagraphPropKey[] = [
      'indentationFirstLine',
      'indentationLeft',
      'indentationRight',
      'lineSpacing',
      'spacingAfter',
      'spacingBefore',
    ];
    const keysInNewProps = keys(newProps).filter(key => keysToValidate.includes(key));

    if (isEmpty(keysInNewProps)) return true;

    const propsToValidate = pick(keysInNewProps, newProps);
    const spacingAfter = propsToValidate?.spacingAfter;
    const spacingBefore = propsToValidate?.spacingBefore;
    const lineSpacing = propsToValidate?.lineSpacing;
    const indentationValid = Queries.indentationValid(editor, paragraph, {
      indentationProperties: {
        firstLine: propsToValidate.indentationFirstLine,
        left: propsToValidate.indentationLeft,
        right: propsToValidate.indentationRight,
      },
    });

    if (!indentationValid) return false;

    // ToDo:  Should we have a max value for spacing before/after?  What should the max be?
    return !(
      (spacingAfter && spacingAfter < 0) ||
      (spacingBefore && spacingBefore < 0) ||
      (lineSpacing && lineSpacing < 0)
    );
  },

  /**
   * Returns the paragraph entries at a location.
   *
   * The `listMarker` option indicates whether we should query list markers, if
   * `at` is a list marker selection.
   */
  *paragraphsAtLocation(
    editor: Editor,
    options: {
      at?: Location;
      listMarker?: boolean;
      match?: NodeMatch;
    }
  ): Generator<NodeEntry<ParagraphElement>, void, undefined> {
    const { at = editor.selection, listMarker = false, match = always(true) } = options;
    if (!at) return;

    if (at === editor.selection && editor.selection.listMarker && listMarker) {
      const listItemEntry = Queries.paragraphAtPoint(editor, { at: editor.selection.focus });
      if (!listItemEntry) return;

      const [listItem] = listItemEntry;
      const activeListItemProps = Queries.renderedParagraphListProps(listItem);
      if (!activeListItemProps) return;

      yield* Editor.nodes<ParagraphElement>(editor, {
        at: [],
        match: node => {
          if (!isParagraphElement(node)) return false;

          const nodeListProps = Queries.renderedParagraphListProps(node);
          if (!nodeListProps) return false;

          return (
            nodeListProps.listId === activeListItemProps.listId &&
            nodeListProps.listLevel === activeListItemProps.listLevel &&
            match(node)
          );
        },
      });
    }

    yield* Queries.nodes<ParagraphElement>(editor, {
      at,
      match: node => isParagraphElement(node) && match(node),
    });
  },

  /**
   * Returns a boolean indicating whether the Range extends across multiple paragraphs.
   */
  rangeAcrossParagraphs: (
    editor: Editor,
    options: {
      at?: Range;
    } = {}
  ): boolean => {
    const { at = editor.selection } = options;
    if (!at) return false;

    const anchorParagraphEntry = Editor.above<ParagraphElement>(editor, {
      at: at.anchor,
      match: node => Element.isElement(node) && node.type === paragraphType,
    });
    const focusParagraphEntry = Editor.above<ParagraphElement>(editor, {
      at: at.focus,
      match: node => Element.isElement(node) && node.type === paragraphType,
    });
    if (!anchorParagraphEntry || !focusParagraphEntry) return false;

    const [, anchorParagraphPath] = anchorParagraphEntry;
    const [, focusParagraphPath] = focusParagraphEntry;

    return !equals(anchorParagraphPath, focusParagraphPath);
  },

  /**
   * Returns the active paragraph alignment, taking into account suggested edits.
   */
  renderedParagraphAlignment: (paragraph: ParagraphProps): AlignmentType => {
    return applySuggestions(paragraph).alignment ?? 'left';
  },

  /**
   * Layout inset for one border side: thickness + OOXML `w:space` (distance from
   * text to the border's inner edge).
   */
  renderedParagraphBorderLayoutInset(
    border: Partial<TableBorder> | undefined,
    unit: Length.Unit = 'px'
  ): number {
    if (!border) return 0;
    const width = Queries.renderedParagraphBorderWidth(border, unit);
    if (!width) return 0;

    return width + convertUnit(border.space ?? 0, 'pt', unit);
  },

  /**
   * Insets for drawn border sides. Top/bottom are thickness + space (height /
   * content offset). Left/right are space only (paint offset into the margin).
   * Top is omitted on non-first split fragments; bottom on non-last fragments.
   */
  renderedParagraphBorderLayoutInsets(options: {
    bordersToDraw: ParagraphBorders | undefined;
    isLastFragment?: boolean;
    splitIndex?: number;
    unit?: Length.Unit;
  }): ParagraphBorderLayoutInsets {
    const { bordersToDraw, isLastFragment = true, splitIndex = 0, unit = 'px' } = options;
    const empty = { bottom: 0, left: 0, right: 0, top: 0 };
    if (!bordersToDraw) return empty;

    function horizontalPaintOffset(border: Partial<TableBorder> | undefined): number {
      if (!border || !Queries.renderedParagraphBorderWidth(border, unit)) return 0;
      return convertUnit(border.space ?? 0, 'pt', unit);
    }

    const top =
      splitIndex === 0 ? Queries.renderedParagraphBorderLayoutInset(bordersToDraw.top, unit) : 0;
    const bottom = isLastFragment
      ? Queries.renderedParagraphBorderLayoutInset(bordersToDraw.bottom, unit)
      : 0;

    return {
      bottom,
      left: horizontalPaintOffset(bordersToDraw.left),
      right: horizontalPaintOffset(bordersToDraw.right),
      top,
    };
  },

  /**
   * How far the border box extends left of the paragraph's content box (a
   * negative px offset, or 0). Word stretches paragraph borders to cover a
   * hanging first line — where a list number sits — so the border's left edge
   * is at min(leftIndent, leftIndent + firstLineIndent).
   */
  renderedParagraphBorderLeftExtent(paragraph: ParagraphProps, unit: Length.Unit = 'px'): number {
    return Math.min(0, Queries.renderedParagraphFirstLineIndent(paragraph, unit));
  },

  renderedParagraphBorderWidth(border: Partial<TableBorder>, unit: Length.Unit = 'px'): number {
    return Queries.renderedTableBorderWidth(Queries.mergeTableBorderWithDefault(border), unit);
  },

  /**
   * Returns the active borders for a paragraph, taking into account suggested
   * edits.
   */
  renderedParagraphBorders: (paragraph: ParagraphProps): ParagraphProps['borders'] => {
    return applySuggestions(paragraph).borders;
  },

  /**
   * Returns the active first line indent, taking into account suggested edits.
   */
  renderedParagraphFirstLineIndent: (
    paragraph: ParagraphProps,
    unit: Length.Unit = 'in'
  ): number => {
    const { indentationFirstLine = 0 } = applySuggestions(paragraph);
    return convertUnit(indentationFirstLine, 'in', unit);
  },

  /**
   * Returns the active left indent, taking into account suggested edits.
   */
  renderedParagraphLeftIndent: (paragraph: ParagraphProps, unit: Length.Unit = 'in'): number => {
    const { indentationLeft = 0 } = applySuggestions(paragraph);
    return convertUnit(indentationLeft, 'in', unit);
  },

  /**
   * Returns the active line spacing as a unit/multiplier = e.g., 1, 1.15, etc.
   */
  renderedParagraphLineSpacing: (
    paragraph: Pick<ParagraphElement, 'lineSpacing' | 'suggestedEdits'>
  ): number => {
    return applySuggestions(paragraph).lineSpacing ?? 1.15;
  },

  /**
   * Returns the active list level, if any, for the given paragraph.
   */
  renderedParagraphListLevel: (paragraph: ParagraphProps): ListLevel | null => {
    return applySuggestions(paragraph).listProps?.listLevel ?? null;
  },

  /**
   * Returns active list props for the given paragraph.
   */
  renderedParagraphListProps: (
    paragraph: Pick<ParagraphProps, 'listProps' | 'suggestedEdits'>
  ): ParagraphElement['listProps'] => {
    return applySuggestions(paragraph).listProps;
  },

  /**
   * Returns the active right indent, taking into account suggested edits.
   */
  renderedParagraphRightIndent: (paragraph: ParagraphProps, unit: Length.Unit = 'in'): number => {
    const { indentationRight = 0 } = applySuggestions(paragraph);
    return convertUnit(indentationRight, 'in', unit);
  },

  /**
   * Returns the active shade for a paragraph, taking into account suggested
   * edits.
   */
  renderedParagraphShade: (paragraph: ParagraphProps): ParagraphProps['shade'] => {
    return applySuggestions(paragraph).shade;
  },

  /**
   * Returns the active spacing after for a paragraph, taking into account suggested edits.
   */
  renderedParagraphSpacingAfter: (
    paragraph: SpacingAfterProps,
    unit: Length.Unit = 'pt'
  ): number => {
    if (paragraph.spacingAfterSuppressed) return 0;

    const { spacingAfter = 0 } = applySuggestions(paragraph);
    return convertUnit(spacingAfter, 'pt', unit);
  },

  /**
   * Returns the active spacing before for a paragraph, taking into account suggested edits.
   */
  renderedParagraphSpacingBefore: (
    paragraph: SpacingBeforeProps,
    unit: Length.Unit = 'pt'
  ): number => {
    if (paragraph.spacingBeforeSuppressed) return 0;

    const { spacingBefore = 0 } = applySuggestions(paragraph);
    return convertUnit(spacingBefore, 'pt', unit);
  },

  /**
   * Returns the active style variant for a paragraph, taking into account suggested edits.
   */
  renderedParagraphStyleVariant: (paragraph: ParagraphProps): ParagraphStyleVariant => {
    return applySuggestions(paragraph).variant ?? defaultStyleVariant;
  },

  /**
   * Returns a paragraph's width.
   */
  renderedParagraphWidth: (
    options: {
      containerWidthPx: number;
      paragraphProps: ParagraphProps;
    },
    unit: Length.Unit
  ): number => {
    const { containerWidthPx, paragraphProps } = options;
    const leftIndent = Queries.renderedParagraphLeftIndent(paragraphProps, unit);
    const rightIndent = Queries.renderedParagraphRightIndent(paragraphProps, unit);
    return convertUnit(containerWidthPx, 'px', unit) - leftIndent - rightIndent;
  },
};
